import { useTexture } from '@react-three/drei';
import { GroupProps, MeshProps, useFrame, useThree } from '@react-three/fiber';
import { forwardRef, useMemo, useRef } from 'react';
import {
  DoubleSide,
  Euler,
  Group,
  Mesh,
  NearestFilter,
  PerspectiveCamera,
  PlaneGeometry,
  Quaternion,
  Uniform,
  Vector3,
} from 'three';

import { publicAssets } from 'utils/gcs';
import { createNoise3D } from 'utils/noise';

import fragmentShader from './leaf.frag';
import vertexShader from './leaf.vert';

interface LeafUserData {
  velocity: Vector3;
  simulated: boolean;
  delayId: number | null;
  quat: Quaternion;
}

interface LeafProps extends MeshProps {
  size: number;
  userData: LeafUserData;
}

const Leaf = forwardRef<Mesh, LeafProps>(({ size, ...props }, ref) => {
  const textureURLs = useMemo(() => {
    const urlset = [
      [
        publicAssets('/effects/leaf-front.png'),
        publicAssets('/effects/leaf-back.png'),
      ],
      [publicAssets('/effects/leaf_01.png')],
      [publicAssets('/effects/leaf_22.png')],
      [publicAssets('/effects/leaf_24.png')],
    ];
    return urlset[Math.trunc(Math.random() * urlset.length)];
  }, []);

  const textures = useTexture(textureURLs, ts => {
    for (const t of ts) {
      t.minFilter = NearestFilter;
      t.magFilter = NearestFilter;
    }
  });

  const geometry = useMemo(() => {
    const plane = new PlaneGeometry(size, size, 2, 2);
    const positions = plane.getAttribute('position').array;

    // x: left; y: bottom
    positions[6 * 3 + 2] = (Math.random() - 0.5) * 0.05;
    // x: right; y: bottom
    positions[8 * 3 + 2] = (Math.random() - 0.5) * 0.05;

    // x: mid; y: bottom
    positions[7 * 3 + 2] = 0;

    // x: left; y: mid
    positions[3 * 3 + 2] =
      positions[6 * 3 + 2] * 2.0 + (Math.random() - 0.5) * 0.01;
    // x: right; y: mid
    positions[5 * 3 + 2] =
      positions[8 * 3 + 2] * 2.0 + (Math.random() - 0.5) * 0.01;

    // x: mid; y: mid
    positions[4 * 3 + 2] = (positions[3 * 3 + 2] + positions[5 * 3 + 2]) / 2.0;

    // x: left; y: top
    positions[0 * 3 + 2] =
      positions[3 * 3 + 2] * 2.0 + (Math.random() - 0.5) * 0.01;
    // x: right; y: top
    positions[2 * 3 + 2] =
      positions[5 * 3 + 2] * 2.0 + (Math.random() - 0.5) * 0.01;

    // x: mid; y: top
    positions[1 * 3 + 2] = (positions[0 * 3 + 2] + positions[2 * 3 + 2]) / 2.0;

    plane.computeVertexNormals();
    return plane;
  }, [size]);

  return (
    <mesh geometry={geometry} ref={ref} {...props}>
      <shaderMaterial
        fragmentShader={fragmentShader}
        vertexShader={vertexShader}
        uniforms={{
          uFrontTexture: new Uniform(textures.at(0)),
          uBackTexture: new Uniform(textures.at(-1)),
        }}
        side={DoubleSide}
        transparent={true}
        depthWrite={false}
      />
    </mesh>
  );
});
Leaf.displayName = 'Leaf';

const gravity = new Vector3(0, -1, 0);
const noise = createNoise3D();
const bounds = {
  min() {
    return new Vector3(-3, -3, -3);
  },
  max() {
    return new Vector3(4, 4, 4);
  },
  normalized(pos: Vector3) {
    const min = this.min();
    const max = this.max();
    const range = max.clone().sub(min);
    return pos.clone().sub(min).divide(range);
  },
  isWithin(pos: Vector3): boolean {
    const min = this.min();
    const max = this.max();
    const withinX = pos.x >= min.x && pos.x <= max.x;
    const withinY = pos.y >= min.y && pos.y <= max.y;
    const withinZ = pos.z >= min.z && pos.z <= max.z;
    return withinX && withinY && withinZ;
  },
};

const randomLeafPosition = (camera: PerspectiveCamera): Vector3 => {
  const ndc = new Vector3(-1, 0, 0).unproject(camera);
  const dir = ndc.clone().sub(camera.position).normalize();
  const angle = new Vector3(0, 0, 1).angleTo(dir);
  const distance = -camera.position.z / Math.cos(angle);
  const offset = dir.clone().multiplyScalar(distance);
  const res = camera.position.clone().add(offset);

  res.x = Math.min(3, res.x + 0.5);
  res.y += Math.random() * 2;
  res.z += Math.random() * -1;

  return res;
};

const randomEuler = (): Euler => {
  return new Euler(
    Math.random() * 2 * Math.PI,
    Math.random() * 2 * Math.PI,
    Math.random() * 2 * Math.PI
  );
};

const randomQuat = (): Quaternion => {
  return new Quaternion().random();
};

const incrementQuat = new Quaternion();
const identityQuat = new Quaternion();

export interface LeavesProps extends GroupProps {
  count?: number;
}

export const Leaves = forwardRef<Group, LeavesProps>(
  ({ count = 300, ...props }, ref) => {
    const camera = useThree(state => state.camera) as PerspectiveCamera;
    const leavesRef = useRef<(Mesh & { userData: LeafUserData })[]>([]);

    const leaves = useMemo(() => {
      return [...Array(count).keys()].map((_, i) => (
        <Leaf
          key={i}
          size={0.08 + Math.random() * 0.04}
          position={randomLeafPosition(camera)}
          rotation={randomEuler()}
          userData={{
            velocity: new Vector3(0, 0, 0),
            simulated: false,
            delayId: null,
            quat: randomQuat(),
          }}
          ref={ref => ref && (leavesRef.current[i] = ref as any)}
        />
      ));
    }, [count, camera]);

    useFrame(({ clock }, deltaTime) => {
      leavesRef.current.forEach(leaf => {
        if (!bounds.isWithin(leaf.position)) {
          leaf.userData.velocity.set(0, 0, 0);
          leaf.position.copy(randomLeafPosition(camera));
          leaf.rotation.copy(randomEuler());
        }

        if (!leaf.userData.simulated) {
          if (!leaf.userData.delayId) {
            leaf.userData.delayId = setTimeout(() => {
              leaf.userData.simulated = true;
            }, 6000 * Math.random()) as unknown as number;
          }
          return;
        }

        // Gravity
        const g = gravity.clone().multiplyScalar(deltaTime);
        leaf.userData.velocity.add(g);

        // Drag
        const vDir = leaf.userData.velocity.clone().normalize();
        const leafNormal = new Vector3(0, 0, 1)
          .applyQuaternion(leaf.quaternion)
          .normalize();
        const dragCoefficient = Math.abs(vDir.dot(leafNormal));

        const drag = vDir
          .clone()
          .multiplyScalar(-dragCoefficient)
          .multiplyScalar(leaf.userData.velocity.lengthSq())
          .multiplyScalar(deltaTime * 10);
        leaf.userData.velocity.add(drag);

        // Wind
        const nv = bounds.normalized(leaf.position);

        const n =
          noise(nv.x + clock.getElapsedTime() * 0.1, nv.y * 6, nv.z * 12) + 1;

        const wind = new Vector3(-Math.random() * 6, 0, 0.2)
          .normalize()
          .multiplyScalar(n * 0.07);
        leaf.userData.velocity.add(wind);

        // Apply velocity
        leaf.position.add(
          leaf.userData.velocity.clone().multiplyScalar(deltaTime)
        );

        // Apply angular velocity
        const incQuat = incrementQuat.copy(leaf.userData.quat);
        incQuat.slerp(identityQuat, (2 - n) / 2.0);
        incQuat.slerp(identityQuat, 1 - deltaTime);
        leaf.quaternion.multiply(incQuat);
      });
    });

    return (
      <group ref={ref} {...props}>
        {leaves}
      </group>
    );
  }
);
Leaves.displayName = 'Leaves';
